<template>
  <div class="agent-workflows">
    <div class="flex h-full gap-1">
      <div class="w-80 min-w-80 border flex flex-col p-1">
        <el-button
          size="mini"
          class="w-full"
          type="primary"
          plain
          @click="addWorkflow"
        >
          New Workflow
        </el-button>
        <draggable
          v-model="workflows"
          tag="ul"
          class="trigger-marker grow py-1"
          ghost-class="ghost"
          handle=".workflow-handle"
          @change="sortWorkflows"
        >
          <li
            v-for="item in workflows"
            :key="item.id"
            class="flex items-center justify-between cursor-move"
            :class="{
              inactive: !item.active,
              active: workflow.id === item.id,
            }"
            @click="selectWorkflow(item)"
          >
            <div class="flex items-center gap-1 flex-1">
              <a class="workflow-handle cursor-move mr-1">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 25 25"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M9.79492 5.17871C9.79492 6.28326 8.89947 7.17871 7.79492 7.17871C6.69037 7.17871 5.79492 6.28326 5.79492 5.17871C5.79492 4.07414 6.69037 3.17871 7.79492 3.17871C8.89947 3.17871 9.79492 4.07414 9.79492 5.17871ZM7.79492 14.1787C8.89947 14.1787 9.79492 13.2833 9.79492 12.1787C9.79492 11.0742 8.89947 10.1787 7.79492 10.1787C6.69037 10.1787 5.79492 11.0742 5.79492 12.1787C5.79492 13.2833 6.69037 14.1787 7.79492 14.1787ZM7.79492 21.1787C8.89947 21.1787 9.79492 20.2833 9.79492 19.1787C9.79492 18.0742 8.89947 17.1787 7.79492 17.1787C6.69037 17.1787 5.79492 18.0742 5.79492 19.1787C5.79492 20.2833 6.69037 21.1787 7.79492 21.1787Z"
                    fill="#828282"
                  />
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M18.7949 5.17871C18.7949 6.28326 17.8995 7.17871 16.7949 7.17871C15.6904 7.17871 14.7949 6.28326 14.7949 5.17871C14.7949 4.07414 15.6904 3.17871 16.7949 3.17871C17.8995 3.17871 18.7949 4.07414 18.7949 5.17871ZM16.7949 14.1787C17.8995 14.1787 18.7949 13.2833 18.7949 12.1787C18.7949 11.0742 17.8995 10.1787 16.7949 10.1787C15.6904 10.1787 14.7949 11.0742 14.7949 12.1787C14.7949 13.2833 15.6904 14.1787 16.7949 14.1787ZM16.7949 21.1787C17.8995 21.1787 18.7949 20.2833 18.7949 19.1787C18.7949 18.0742 17.8995 17.1787 16.7949 17.1787C15.6904 17.1787 14.7949 18.0742 14.7949 19.1787C14.7949 20.2833 15.6904 21.1787 16.7949 21.1787Z"
                    fill="#828282"
                  />
                </svg>
              </a>
              <div>{{ item.name }}</div>
            </div>
            <div class="flex items-center">
              <i class="fas fa-comment green" v-if="item.interactive" />
              <i class="fas fa-ban red" v-if="!item.active" />
            </div>
          </li>
        </draggable>
      </div>
      <div class="grow border">
        <div
          v-if="!workflow.hasOwnProperty('id')"
          class="text-gray-400 text-center py-8"
        >
          Select a workflow or create a new one
        </div>
        <div v-else class="h-full grid grid-cols-2 gap-2 divide-x">
          <div class="h-full overflow-auto p-2">
            <el-form
              ref="workflowForm"
              v-model="workflowForm"
              size="mini"
              label-position="top"
              class="agent-workflow-form"
              :disabled="isTesting"
            >
              <div class="box">
                <div class="box-title flex items-center justify-between">
                  <div>Workflow: {{ workflow.name }}</div>
                  <div class="flex items-center gap-1">
                    <el-checkbox v-model="workflowForm.interactive" size="mini">
                      Interactive
                    </el-checkbox>
                    <el-checkbox v-model="workflowForm.active" size="mini">
                      Active
                    </el-checkbox>
                  </div>
                </div>
                <div class="box-content">
                  <div class="grid grid-cols-4 gap-1">
                    <el-form-item label="Name" required class="col-span-3">
                      <el-input
                        v-model="workflowForm.name"
                        placeholder="Workflow name"
                      />
                    </el-form-item>
                    <el-form-item label="Identify">
                      <el-input
                        v-model="workflowForm.identify"
                        placeholder="Identify"
                        class="nospace"
                      />
                    </el-form-item>
                  </div>
                  <label class="title">
                    <div class="text-base">Trigger</div>
                  </label>

                  <div class="grid grid-cols-4 gap-1">
                    <el-form-item label="Types" required class="col-span-3">
                      <el-select
                        v-model="workflowForm.trigger.types"
                        multiple
                        placeholder="Select trigger types"
                        style="width: 100%"
                      >
                        <el-option label="Cron" value="cron" />
                        <el-option label="Events" value="events" />
                        <el-option label="Manual" value="manual" />
                      </el-select>
                    </el-form-item>
                    <el-form-item label="Frequent Interval">
                      <el-input
                        v-model="workflowForm.trigger.frequent_interval"
                        placeholder="300"
                      />
                    </el-form-item>
                  </div>

                  <el-form-item
                    v-if="hasCronTrigger"
                    label="Cron Expression"
                    required
                  >
                    <el-input
                      v-model="workflowForm.trigger.cron"
                      placeholder="0 5 * * *"
                    />
                    <div class="text-xs text-gray-500 mt-1">
                      Format: minute hour day month weekday (e.g., "0 5 * * *"
                      for daily at 5 AM)
                    </div>
                  </el-form-item>

                  <div v-if="hasEventsTrigger" class="mt-2">
                    <el-form-item label="Events" required>
                      <el-select
                        v-model="workflowForm.trigger.events"
                        multiple
                        filterable
                        placeholder="Type and press Enter to add event names (e.g., check_task)"
                        style="width: 100%"
                      >
                        <el-option
                          v-for="event in options.events"
                          :key="event"
                          :label="event.titleize()"
                          :value="event"
                        />
                      </el-select>
                      <div class="text-xs text-gray-500 mt-1">
                        Enter event names that will trigger this workflow. Type
                        and press Enter to add multiple events.
                      </div>
                    </el-form-item>
                  </div>

                  <label class="title">
                    <div class="text-base">Iterate</div>
                  </label>
                  <el-form-item>
                    <el-checkbox v-model="workflowForm.iterate.enabled">
                      Enable iteration over final step output
                    </el-checkbox>
                    <div
                      v-if="workflowForm.iterate.enabled"
                      class="mt-2 space-y-2 border rounded p-3 bg-slate-50"
                    >
                      <el-form-item label="Iterate Workflow" required>
                        <el-select
                          v-model="workflowForm.iterate.workflow_id"
                          filterable
                          placeholder="Select workflow to call for each item"
                          style="width: 100%"
                        >
                          <el-option
                            v-for="wf in availableIterateWorkflows"
                            :key="wf.id"
                            :label="wf.name"
                            :value="wf.id"
                          />
                        </el-select>
                        <div class="text-xs text-gray-500 mt-1">
                          This workflow will be called for each item in the last
                          step's output array. The item will be passed as
                          <code>input</code> to the iterate workflow. Only
                          workflows without iteration enabled and without cron
                          triggers are shown. Use "Manual" trigger type for
                          iterate workflows (they're called on-demand, not
                          scheduled).
                        </div>
                      </el-form-item>
                    </div>
                  </el-form-item>
                  <label class="title">
                    <div class="text-base">Steps</div>
                    <div class="flex items-center gap-2">
                      <a
                        class="handle cursor-move"
                        :class="{ green: stepsSortable, gray: !stepsSortable }"
                        @click="stepsSortable = !stepsSortable"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 25 25"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M9.79492 5.17871C9.79492 6.28326 8.89947 7.17871 7.79492 7.17871C6.69037 7.17871 5.79492 6.28326 5.79492 5.17871C5.79492 4.07414 6.69037 3.17871 7.79492 3.17871C8.89947 3.17871 9.79492 4.07414 9.79492 5.17871ZM7.79492 14.1787C8.89947 14.1787 9.79492 13.2833 9.79492 12.1787C9.79492 11.0742 8.89947 10.1787 7.79492 10.1787C6.69037 10.1787 5.79492 11.0742 5.79492 12.1787C5.79492 13.2833 6.69037 14.1787 7.79492 14.1787ZM7.79492 21.1787C8.89947 21.1787 9.79492 20.2833 9.79492 19.1787C9.79492 18.0742 8.89947 17.1787 7.79492 17.1787C6.69037 17.1787 5.79492 18.0742 5.79492 19.1787C5.79492 20.2833 6.69037 21.1787 7.79492 21.1787Z"
                            fill="currentColor"
                          />
                          <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M18.7949 5.17871C18.7949 6.28326 17.8995 7.17871 16.7949 7.17871C15.6904 7.17871 14.7949 6.28326 14.7949 5.17871C14.7949 4.07414 15.6904 3.17871 16.7949 3.17871C17.8995 3.17871 18.7949 4.07414 18.7949 5.17871ZM16.7949 14.1787C17.8995 14.1787 18.7949 13.2833 18.7949 12.1787C18.7949 11.0742 17.8995 10.1787 16.7949 10.1787C15.6904 10.1787 14.7949 11.0742 14.7949 12.1787C14.7949 13.2833 15.6904 14.1787 16.7949 14.1787ZM16.7949 21.1787C17.8995 21.1787 18.7949 20.2833 18.7949 19.1787C18.7949 18.0742 17.8995 17.1787 16.7949 17.1787C15.6904 17.1787 14.7949 18.0742 14.7949 19.1787C14.7949 20.2833 15.6904 21.1787 16.7949 21.1787Z"
                            fill="currentColor"
                          />
                        </svg>
                      </a>
                      <a class="fas fa-plus-circle" @click="addStep" />
                    </div>
                  </label>
                  <el-form-item>
                    <draggable
                      v-model="workflowForm.steps"
                      class="flex flex-col gap-2"
                      ghost-class="ghost"
                      handle=".handle"
                      :disabled="!stepsSortable"
                    >
                      <div
                        v-for="(step, index) in workflowForm.steps"
                        :key="index"
                        class="p-2 border rounded bg-slate-50"
                      >
                        <div
                          class="flex justify-between items-center border-b -m-2 px-2 py-1 bg-slate-100"
                        >
                          <div class="flex items-center gap-1">
                            <a class="handle cursor-move" v-if="stepsSortable">
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 25 25"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  fill-rule="evenodd"
                                  clip-rule="evenodd"
                                  d="M9.79492 5.17871C9.79492 6.28326 8.89947 7.17871 7.79492 7.17871C6.69037 7.17871 5.79492 6.28326 5.79492 5.17871C5.79492 4.07414 6.69037 3.17871 7.79492 3.17871C8.89947 3.17871 9.79492 4.07414 9.79492 5.17871ZM7.79492 14.1787C8.89947 14.1787 9.79492 13.2833 9.79492 12.1787C9.79492 11.0742 8.89947 10.1787 7.79492 10.1787C6.69037 10.1787 5.79492 11.0742 5.79492 12.1787C5.79492 13.2833 6.69037 14.1787 7.79492 14.1787ZM7.79492 21.1787C8.89947 21.1787 9.79492 20.2833 9.79492 19.1787C9.79492 18.0742 8.89947 17.1787 7.79492 17.1787C6.69037 17.1787 5.79492 18.0742 5.79492 19.1787C5.79492 20.2833 6.69037 21.1787 7.79492 21.1787Z"
                                  fill="#828282"
                                />
                                <path
                                  fill-rule="evenodd"
                                  clip-rule="evenodd"
                                  d="M18.7949 5.17871C18.7949 6.28326 17.8995 7.17871 16.7949 7.17871C15.6904 7.17871 14.7949 6.28326 14.7949 5.17871C14.7949 4.07414 15.6904 3.17871 16.7949 3.17871C17.8995 3.17871 18.7949 4.07414 18.7949 5.17871ZM16.7949 14.1787C17.8995 14.1787 18.7949 13.2833 18.7949 12.1787C18.7949 11.0742 17.8995 10.1787 16.7949 10.1787C15.6904 10.1787 14.7949 11.0742 14.7949 12.1787C14.7949 13.2833 15.6904 14.1787 16.7949 14.1787ZM16.7949 21.1787C17.8995 21.1787 18.7949 20.2833 18.7949 19.1787C18.7949 18.0742 17.8995 17.1787 16.7949 17.1787C15.6904 17.1787 14.7949 18.0742 14.7949 19.1787C14.7949 20.2833 15.6904 21.1787 16.7949 21.1787Z"
                                  fill="#828282"
                                />
                              </svg>
                            </a>
                            <div class="text-sm">Step {{ index + 1 }}</div>
                          </div>
                          <div class="flex items-center gap-2">
                            <div class="tag">{{ step.name }}</div>
                            <a
                              class="fas fa-trash-alt red text-xs"
                              @click="removeStep(index)"
                            />
                          </div>
                        </div>
                        <div v-show="!stepsSortable" class="mt-4">
                          <div class="grid grid-cols-4 gap-1">
                            <el-form-item
                              label="Name"
                              required
                              class="col-span-2"
                            >
                              <el-input
                                v-model="step.name"
                                placeholder="Step name"
                              />
                            </el-form-item>

                            <el-form-item label="Type" required>
                              <el-select
                                v-model="step.type"
                                placeholder="Select step type"
                                style="width: 100%"
                              >
                                <el-option label="SQL" value="sql" />
                                <el-option label="Bridge" value="bridge" />
                                <el-option label="LLM" value="llm" />
                              </el-select>
                            </el-form-item>

                            <el-form-item label="Output Key">
                              <el-input
                                v-model="step.output_key"
                                placeholder="e.g., import_orders"
                              />
                            </el-form-item>
                          </div>
                          <el-form-item
                            v-if="step.type === 'sql'"
                            label="SQL Query"
                            required
                          >
                            <el-input
                              v-model="step.sql"
                              type="textarea"
                              :rows="16"
                              placeholder="SELECT COUNT(*) > 0 AS continue FROM ..."
                            />
                          </el-form-item>
                          <el-form-item
                            v-if="step.type === 'sql'"
                            label="Conditional Step"
                          >
                            <el-checkbox v-model="step.conditional">
                              Stop workflow if SQL returns false/0
                            </el-checkbox>
                            <div
                              v-if="step.conditional"
                              class="hl leading-4 p-1 rounded text-xs"
                            >
                              SQL should return boolean (e.g., SELECT COUNT(*) >
                              0 AS continue) or numeric value. If conditional is
                              enabled, workflow stops if result is false or 0.
                            </div>
                          </el-form-item>
                          <el-form-item
                            v-if="step.type === 'bridge'"
                            label="Bridge Method"
                            required
                          >
                            <el-input
                              v-model="step.bridge"
                              placeholder="e.g., Agent::Bridge.order_details"
                            />
                            <div class="text-xs text-gray-500 mt-1">
                              Ruby method to call (e.g.,
                              Agent::Bridge.order_details). The method will
                              receive input_map values as arguments.
                            </div>
                          </el-form-item>
                          <el-form-item
                            v-if="step.type === 'llm'"
                            label="System Prompt"
                          >
                            <el-input
                              v-model="step.system_prompt"
                              type="textarea"
                              :rows="8"
                              placeholder="Enter system prompt (no variables allowed)..."
                            />
                            <div class="text-xs text-gray-500 mt-1">
                              System prompt used to create/update the
                              conversation. No variables allowed.
                            </div>
                          </el-form-item>
                          <el-form-item
                            v-if="step.type === 'llm'"
                            label="User Prompt"
                            required
                          >
                            <el-input
                              v-model="step.user_prompt"
                              type="textarea"
                              :rows="8"
                              placeholder="Enter user prompt (variables allowed, e.g., variable_name)..."
                            />
                            <div class="text-xs text-gray-500 mt-1">
                              User prompt used in the response API. Variables
                              can be used with
                              <b class="text-sm blue">
                                <span>{{</span>variable_name<span>}}</span>
                              </b>
                              syntax.
                            </div>
                          </el-form-item>
                          <el-form-item>
                            <slot slot="label">
                              <a
                                class="fas fa-plus-circle"
                                @click="addInputMapEntry(step)"
                              />
                              Input Map
                            </slot>
                            <div class="w-full flex flex-col gap-1">
                              <div
                                v-for="(key, mapIndex) in step.input_map_keys ||
                                []"
                                :key="mapIndex"
                                class="flex gap-1"
                              >
                                <el-input
                                  v-model="step.input_map_keys[mapIndex]"
                                  placeholder="Key"
                                  @input="
                                    () => updateInputMapKey(step, mapIndex)
                                  "
                                />
                                <el-input
                                  v-model="step.input_map_values[mapIndex]"
                                  placeholder="Value (e.g., ruby:today or context:key)"
                                  @input="
                                    () => updateInputMapValue(step, mapIndex)
                                  "
                                />
                                <el-button
                                  type="text"
                                  @click="removeInputMapEntry(step, mapIndex)"
                                >
                                  <div class="fas fa-trash-alt red" />
                                </el-button>
                              </div>
                            </div>
                          </el-form-item>
                        </div>
                      </div>
                    </draggable>
                  </el-form-item>
                  <slot v-if="!workflowForm.iterate?.enabled">
                    <label class="title">
                      <div class="text-base">Output</div>
                    </label>
                    <el-form-item label="Slim Template">
                      <el-input
                        v-model="workflowForm.output.template"
                        type="textarea"
                        :rows="12"
                      />
                      <div class="text-xs text-gray-500 mt-1">
                        Use Slim template syntax. Access step outputs via
                        <code>@context.output_key</code>. Example:
                        <code>@context.street_turns</code> for a step with
                        <code>output_key: "street_turns"</code>. You can use
                        Rails helpers like <code>link_to</code>,
                        <code>container_path</code>, etc.
                      </div>
                    </el-form-item>
                  </slot>
                  <label class="title">
                    <div class="text-base">Target</div>
                  </label>
                  <el-form-item label="Mapping">
                    <el-input
                      v-model="workflowForm.target.mapping"
                      placeholder="e.g., Container:input:id"
                    />
                    <div class="text-xs text-gray-500 mt-1">
                      Format: ClassName:source:key (e.g., Container:input:id or
                      Container:context:order.id)
                    </div>
                  </el-form-item>
                  <label class="title">
                    <div class="text-base">Test</div>
                  </label>
                  <el-form-item label="Input Data">
                    <el-input
                      v-model="workflowForm.test.input"
                      type="textarea"
                      :rows="2"
                      placeholder='Enter input data in JSON format (e.g., {"id": 123})'
                    />
                    <div class="text-xs text-gray-500 mt-1">
                      Input data (JSON format) used when testing this workflow.
                    </div>
                  </el-form-item>
                </div>
                <div class="box-footer">
                  <div class="flex items-center justify-between">
                    <el-button-group>
                      <el-button
                        type="success"
                        size="mini"
                        @click="testWorkflow"
                        :disabled="!persisted"
                        >Run Test</el-button
                      >
                    </el-button-group>
                    <el-button-group>
                      <el-button
                        type="primary"
                        @click="saveWorkflow"
                        size="mini"
                        >Save</el-button
                      >
                      <el-button
                        type="danger"
                        size="mini"
                        @click="destroyWorkflow"
                        :disabled="!persisted"
                        >Delete</el-button
                      >
                      <el-button size="mini" @click="cancelWorkflow"
                        >Cancel</el-button
                      >
                    </el-button-group>
                  </div>
                </div>
              </div>
            </el-form>
          </div>
          <div class="h-full overflow-auto p-2 test-result">
            <div v-if="workflow.result" class="space-y-4">
              <div class="border rounded p-3 bg-gray-50">
                <div class="flex items-center justify-between mb-2">
                  <h3 class="font-semibold text-lg">
                    {{ workflow.result.name }}
                  </h3>
                  <div class="flex items-center gap-2">
                    <el-tag
                      v-if="
                        workflow.result.started_at && workflow.result.stopped_at
                      "
                      size="small"
                      type="info"
                    >
                      {{
                        calculateDuration(
                          workflow.result.started_at,
                          workflow.result.stopped_at,
                        )
                      }}
                    </el-tag>
                    <el-tag
                      :type="getStatusType(workflow.result.status)"
                      size="small"
                    >
                      {{ workflow.result.status }}
                    </el-tag>
                  </div>
                </div>
                <div v-if="workflow.result.error" class="mt-2">
                  <el-alert
                    :title="workflow.result.error"
                    type="error"
                    :closable="false"
                    show-icon
                  />
                </div>
                <!-- Output HTML (if template was rendered) -->
                <div v-if="workflow.result.output_html" class="output-html">
                  <div class="rounded border bg-white">
                    <div class="font-bold border-b p-1">Output HTML</div>

                    <div
                      class="max-h-60 overflow-auto p-1"
                      v-html="workflow.result.output_html"
                    />
                  </div>
                </div>
                <!-- Steps -->
                <div
                  v-if="!isEmptyData(workflow.result.steps)"
                  class="space-y-3"
                >
                  <div
                    v-for="(step, index) in workflow.result.steps"
                    :key="index"
                    class="border rounded bg-white"
                  >
                    <div
                      class="flex items-center justify-between border-b py-1 px-2"
                    >
                      <div class="flex items-center gap-2">
                        <span class="font-semibold">
                          Step {{ step.index + 1 }}: {{ step.name }}
                        </span>
                        <el-tag size="mini" :type="getStatusType(step.status)">
                          {{ step.type ? step.type.toUpperCase() : "" }}
                        </el-tag>
                      </div>
                      <div class="flex items-center gap-2">
                        <el-tag
                          v-if="step.started_at && step.stopped_at"
                          size="small"
                          type="info"
                        >
                          {{
                            calculateDuration(step.started_at, step.stopped_at)
                          }}
                        </el-tag>
                        <el-tag :type="getStatusType(step.status)" size="small">
                          {{ step.status }}
                        </el-tag>
                      </div>
                    </div>

                    <div v-if="step.error" class="mb-3">
                      <el-alert
                        :title="step.error"
                        type="error"
                        :closable="false"
                        show-icon
                      />
                    </div>

                    <div class="space-y-3 p-2">
                      <!-- Input -->
                      <div v-if="step.input">
                        <div class="text-sm font-semibold mb-1 text-gray-700"
                          >Input:</div
                        >
                        <div
                          class="border rounded p-2 bg-gray-50 max-h-64 overflow-auto"
                        >
                          <vue-json-pretty
                            :data="step.input"
                            :deep="10"
                            :show-icon="true"
                            :show-double-quotes="false"
                            :show-line="true"
                          />
                        </div>
                      </div>

                      <!-- Output -->
                      <div v-if="step.output">
                        <div class="text-sm font-semibold mb-1 text-gray-700">
                          Output:
                        </div>
                        <div
                          class="border rounded p-2 bg-gray-50 max-h-60 overflow-auto"
                        >
                          <vue-json-pretty
                            :data="step.output"
                            :deep="10"
                            :show-icon="true"
                            :show-double-quotes="false"
                            :show-line="true"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="text-gray-400 text-center py-8">
              No result available. Run a test to see results.
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
export default {
  name: "AgentWorkflows",
  props: {
    tenant: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      workflows: [],
      workflow: {},
      workflowForm: {},
      stepsSortable: false,
    };
  },
  async mounted() {
    this.initWorkflowForm();
    await this.loadWorkflows();
  },
  updated() {
    this.$nextTick(() => {
      NApp.autoRemove();
    });
  },
  computed: {
    persisted() {
      return this.workflow.id > 0;
    },
    isTesting() {
      return this.workflow.status === "testing";
    },
    hasCronTrigger() {
      return (
        this.workflowForm.trigger?.types &&
        this.workflowForm.trigger.types.includes("cron")
      );
    },
    hasEventsTrigger() {
      return (
        this.workflowForm.trigger?.types &&
        this.workflowForm.trigger.types.includes("events")
      );
    },
    availableIterateWorkflows() {
      return this.workflows.filter(
        (wf) =>
          (!this.persisted || wf.id !== this.workflow.id) &&
          !wf.iterate?.enabled &&
          !wf.trigger?.types?.includes("cron"),
      );
    },
  },
  methods: {
    initWorkflowForm() {
      this.workflowForm = {
        name: "",
        interactive: false,
        active: false,
        trigger: {
          types: [],
          cron: "",
          events: [],
          frequent_interval: "",
        },
        iterate: {
          enabled: false,
          workflow_id: null,
        },
        steps: [],
        output: {
          template: "",
        },
        test: {
          input: "",
        },
        target: {
          mapping: "",
        },
        options: {},
      };
    },
    async loadWorkflows() {
      try {
        const response = await this.$http.get("/system/agent_workflows", {
          params: { tenant: this.tenant.subdomain },
        });
        this.workflows = response.data.workflows;
        this.options = response.data.options;
      } catch (err) {
        console.error(err);
      }
    },
    addWorkflow() {
      this.workflow = { id: 0 };
      this.workflowForm = {
        name: "",
        active: false,
        trigger: {
          types: [],
          cron: "",
          events: [],
          frequent_interval: "",
        },
        iterate: {
          enabled: false,
          workflow_id: null,
        },
        steps: [],
        output: {
          template: "",
        },
        test: {
          input: "",
        },
        target: {
          mapping: "",
        },
      };
    },
    selectWorkflow(item) {
      this.workflow = item;
      // Handle backward compatibility: convert old trigger.type to new trigger.types array
      const trigger = item.trigger || {};
      let types = trigger.types || [];
      // If old format (type instead of types), convert it
      if (trigger.type && !types.length) {
        types = [trigger.type];
      }

      this.workflowForm = {
        name: item.name || "",
        identify: item.identify || "",
        active: item.active !== undefined ? item.active : false,
        interactive: item.interactive !== undefined ? item.interactive : false,
        trigger: {
          types: types,
          cron: trigger.cron || "",
          events: trigger.events || [],
          frequent_interval: trigger.frequent_interval || "",
        },
        iterate: item.iterate || {
          enabled: false,
          workflow_id: null,
        },
        steps: (item.steps || []).map((step) => {
          const inputMap = step.input_map || {};
          const keys = Object.keys(inputMap);
          const values = keys.map((k) => inputMap[k]);
          return {
            ...step,
            sql: step.sql || "",
            bridge: step.bridge || "",
            system_prompt: step.system_prompt || "",
            user_prompt: step.user_prompt || "",
            input_map: { ...inputMap },
            input_map_keys: keys,
            input_map_values: values,
            conditional: step.conditional || false,
          };
        }),
        output: item.output || { template: "" },
        test: item.test || { input: "" },
        target: item.target || { mapping: "" },
      };
    },
    addStep() {
      this.workflowForm.steps.push({
        name: "",
        type: "sql",
        sql: "",
        bridge: "",
        system_prompt: "",
        user_prompt: "",
        output_key: "",
        input_map: {},
        input_map_keys: [],
        input_map_values: [],
        conditional: false,
      });
    },
    removeStep(index) {
      this.workflowForm.steps.splice(index, 1);
    },
    addInputMapEntry(step) {
      if (!step.input_map) {
        step.input_map = {};
        step.input_map_keys = [];
        step.input_map_values = [];
      }
      step.input_map_keys.push("");
      step.input_map_values.push("");
    },
    removeInputMapEntry(step, index) {
      const key = step.input_map_keys[index];
      if (key && step.input_map[key]) {
        delete step.input_map[key];
      }
      step.input_map_keys.splice(index, 1);
      step.input_map_values.splice(index, 1);
    },
    updateInputMapKey(step, index) {
      // Rebuild input_map from arrays after v-model update
      step.input_map = {};
      if (step.input_map_keys && step.input_map_values) {
        step.input_map_keys.forEach((key, idx) => {
          if (key && step.input_map_values[idx]) {
            step.input_map[key] = step.input_map_values[idx];
          }
        });
      }
    },
    updateInputMapValue(step, index) {
      // Rebuild input_map from arrays after v-model update
      step.input_map = {};
      if (step.input_map_keys && step.input_map_values) {
        step.input_map_keys.forEach((key, idx) => {
          if (key && step.input_map_values[idx]) {
            step.input_map[key] = step.input_map_values[idx];
          }
        });
      }
    },
    prepareWorkflowData() {
      // Return structure matching strong parameters:
      // agent_workflow[name, active, interactive, trigger, iterate, steps, output]
      // Prepare iterate data
      const iterate = this.workflowForm.iterate?.enabled
        ? {
            enabled: true,
            workflow_id: this.workflowForm.iterate.workflow_id,
          }
        : null;
      // Prepare steps with proper structure
      const steps = this.workflowForm.steps.map((step) => {
        const stepData = {
          name: step.name,
          type: step.type,
          output_key: step.output_key,
          input_map: {},
        };
        if (step.type === "sql") {
          stepData.sql = step.sql;
          // Add conditional field for SQL steps
          if (step.conditional !== undefined) {
            stepData.conditional = step.conditional;
          }
        } else if (step.type === "bridge") {
          stepData.bridge = step.bridge || "";
        } else if (step.type === "llm") {
          stepData.system_prompt = step.system_prompt || "";
          stepData.user_prompt = step.user_prompt || "";
        }
        // Rebuild input_map from keys and values
        if (step.input_map_keys && step.input_map_values) {
          step.input_map_keys.forEach((key, idx) => {
            if (key && step.input_map_values[idx]) {
              stepData.input_map[key] = step.input_map_values[idx];
            }
          });
        }
        return stepData;
      });

      return {
        tenant: this.tenant.subdomain,
        agent_workflow: {
          name: this.workflowForm.name,
          identify: this.workflowForm.identify,
          active: this.workflowForm.active,
          interactive: this.workflowForm.interactive,
          trigger: this.workflowForm.trigger,
          iterate: iterate,
          steps: steps,
          output: this.workflowForm.output || { template: "" },
          test: this.workflowForm.test || { input: "" },
          target: this.workflowForm.target || { mapping: "" },
        },
      };
    },
    async saveWorkflow() {
      try {
        const payload = this.prepareWorkflowData();
        const func = this.workflow.id > 0 ? this.$http.put : this.$http.post;
        const url =
          this.workflow.id > 0
            ? `/system/agent_workflows/${this.workflow.id}`
            : `/system/agent_workflows`;
        const response = await func(url, payload);
        this.$message({
          message: "The workflow was saved successfully",
          type: "success",
        });
        await this.loadWorkflows();
        const item = this.workflows.find((o) => o.id === response.data.id);
        if (item) this.selectWorkflow(item);
      } catch (err) {
        this.$message({
          message: err.response?.data?.errors || "Failed to save workflow",
          type: "error",
        });
      }
    },
    cancelWorkflow() {
      this.workflow = {};
      this.initWorkflowForm();
    },
    async destroyWorkflow() {
      try {
        await this.$confirm(
          "Are you sure you want to delete this workflow?",
          "Warning",
          {
            confirmButtonText: "Delete",
            cancelButtonText: "Cancel",
            type: "warning",
          },
        );
        await this.$http.delete(`/system/agent_workflows/${this.workflow.id}`, {
          params: { tenant: this.tenant.subdomain },
        });
        this.$message({
          message: "The workflow was deleted successfully",
          type: "success",
        });
        await this.loadWorkflows();
        this.cancelWorkflow();
      } catch (err) {}
    },
    testingService() {
      return this.$loading.service({
        target: ".test-result",
        lock: true,
        text: "Please wait while running workflow test",
        spinner: "el-icon-loading",
        background: "rgba(255, 255, 255, 0.8)",
      });
    },
    async testWorkflow() {
      let loading = null;
      try {
        await this.$confirm(
          "Are you sure you want to run a test for this workflow?",
          "Warning",
          {
            confirmButtonText: "Run",
            cancelButtonText: "Cancel",
            type: "warning",
          },
        );
        this.$set(this.workflow, "status", "testing");
        this.$set(this.workflow, "result", null);
        loading = this.testingService();
        const response = await this.$http2.post(
          `/system/agent_workflows/${this.workflow.id}/test`,
          { tenant: this.tenant.subdomain },
        );
        this.$set(this.workflow, "result", response.data);
        NApp.dispatchTurboLoad();
      } catch (err) {
        this.$message({
          message: err.response?.data?.errors || "Failed to run test",
          type: "error",
        });
      } finally {
        this.$set(this.workflow, "status", "completed");
        loading?.close();
      }
    },
    formatDateTime(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleString();
    },
    calculateDuration(startedAt, stoppedAt) {
      if (!startedAt || !stoppedAt) return "N/A";
      const start = new Date(startedAt);
      const stop = new Date(stoppedAt);
      const diffMs = stop - start;
      const diffSeconds = Math.floor(diffMs / 1000);
      const diffMinutes = Math.floor(diffSeconds / 60);
      const diffHours = Math.floor(diffMinutes / 60);

      if (diffHours > 0) {
        return `${diffHours}h ${diffMinutes % 60}m ${diffSeconds % 60}s`;
      } else if (diffMinutes > 0) {
        return `${diffMinutes}m ${diffSeconds % 60}s`;
      } else {
        return `${diffSeconds}s`;
      }
    },
    getStatusType(status) {
      if (!status) return "info";
      const statusLower = status.toLowerCase();
      if (statusLower === "success") return "success";
      if (statusLower === "error" || statusLower === "failed") return "danger";
      if (statusLower === "running" || statusLower === "testing")
        return "warning";
      return "info";
    },
    async sortWorkflows() {
      this.workflows.forEach(async (workflow, index) => {
        workflow.seq_num = index + 1;
        await this.$http.put(`/system/agent_workflows/${workflow.id}`, {
          tenant: this.tenant.subdomain,
          agent_workflow: { seq_num: index + 1 },
        });
      });
    },
  },
};
</script>
<style lang="scss" scoped>
.agent-workflows::v-deep {
  @apply h-full;
  ul.trigger-marker,
  .trigger-marker {
    @apply overflow-auto h-full;
  }
  .workflow-handle {
    display: flex;
    align-items: center;
    opacity: 0.5;
    &:hover {
      opacity: 1;
    }
  }
  .agent-workflow-form {
    @apply h-full;
    .box {
      @apply h-full;
      .box-content {
        @apply h-[calc(100%_-_85px)] overflow-auto;
      }
    }
  }
  .ghost {
    opacity: 0.4;
    background: #cbd5e1;
  }
  .handle {
    display: flex;
    align-items: center;
    &:hover {
      opacity: 0.7;
    }
  }
  .output-html {
    @apply my-2;
    table.tbl {
      td {
        @apply font-normal text-xs #{!important};
      }
    }
  }
}
</style>
